import { useState, useEffect, useMemo, useRef } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ExtractionResult } from './VisibleBreakdownScreen';

/* ═══════════════════════════════════════════════════════════════════
 * ProcessingScreen — Live Transformation Visualization
 *
 * Single screen. One dramaturgy:
 *   parsing → identifying → structuring → finalizing → done
 *
 * Shows the user's raw input being understood and decomposed
 * by the Decision Engine in real time.
 * ═══════════════════════════════════════════════════════════════════ */

type TransformStage = 'parsing' | 'identifying' | 'structuring' | 'finalizing';

interface ProcessingScreenProps {
  extraction: ExtractionResult | null;
  rawInput: string;
  hasFiles: boolean;
  onComplete: () => void;
  inputQuality?: 'high' | 'medium' | 'low';
}

const STEPS = [
  'Optionen werden identifiziert',
  'Zielkonflikte werden aufgebaut',
  'Risiken werden abgeleitet',
];

export const ProcessingScreen = ({
  extraction,
  rawInput,
  onComplete,
  inputQuality,
}: ProcessingScreenProps) => {
  const [stage, setStage] = useState<TransformStage>('parsing');
  const [doneSteps, setDoneSteps] = useState<boolean[]>([false, false, false]);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const hasStartedReveal = useRef(false);

  // ── Structured data from extraction ──
  const structured = useMemo(() => {
    if (!extraction) return null;
    const f = extraction.fields;
    return {
      optA: f.optionA?.value as string | undefined,
      optB: f.optionB?.value as string | undefined,
      conflict: f.coreConflict?.value as string | undefined,
      title: extraction.classification?.suggested_title,
    };
  }, [extraction]);

  // ── Display-truncated input ──
  const displayInput = useMemo(() => {
    const text = rawInput.trim();
    if (text.length <= 180) return text;
    return text.slice(0, 180).replace(/\s+\S*$/, '') + '…';
  }, [rawInput]);

  // ── Find highlight fragments in input ──
  const highlights = useMemo(() => {
    if (!structured?.optA && !structured?.optB) return [];
    const result: Array<{ start: number; end: number; label: string }> = [];
    const lower = displayInput.toLowerCase();

    [structured.optA, structured.optB].forEach((opt, idx) => {
      if (!opt) return;
      const words = opt.toLowerCase().split(/\s+/).slice(0, 3).join(' ');
      const pos = lower.indexOf(words);
      if (pos >= 0) {
        let end = pos + words.length;
        while (end < displayInput.length && ![' ', ',', '.', ';'].includes(displayInput[end])) end++;
        result.push({ start: pos, end, label: `Option ${idx === 0 ? 'A' : 'B'}` });
      }
    });

    return result.sort((a, b) => a.start - b.start);
  }, [displayInput, structured]);

  // ── Cleanup ──
  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  // ── Reveal sequence when extraction arrives ──
  useEffect(() => {
    if (!extraction || hasStartedReveal.current) return;
    hasStartedReveal.current = true;

    const t1 = setTimeout(() => {
      setStage('identifying');
      setDoneSteps(([, b, c]) => [true, b, c]);
    }, 400);

    const t2 = setTimeout(() => {
      setStage('structuring');
      setDoneSteps(([a, , c]) => [a, true, c]);
    }, 1000);

    const t3 = setTimeout(() => {
      setStage('finalizing');
      setDoneSteps(() => [true, true, true]);
    }, 1600);

    const t4 = setTimeout(() => onCompleteRef.current(), 2200);

    timersRef.current.push(t1, t2, t3, t4);
  }, [extraction]);

  // ── Render highlighted text ──
  const renderText = () => {
    const showHighlights = stage !== 'parsing' && highlights.length > 0;
    const faded = stage === 'structuring' || stage === 'finalizing';

    if (!showHighlights) {
      return <span className="text-foreground/60">{displayInput}</span>;
    }

    const parts: JSX.Element[] = [];
    let last = 0;

    highlights.forEach((h, i) => {
      if (h.start > last) {
        parts.push(
          <span key={`t${i}`} className={cn('transition-all duration-700', faded ? 'opacity-25' : 'text-foreground/60')}>
            {displayInput.slice(last, h.start)}
          </span>
        );
      }
      parts.push(
        <span key={`h${i}`} className={cn(
          'relative inline transition-all duration-500',
          stage === 'identifying' && 'text-foreground/90 border-b border-primary/25',
          faded && 'text-foreground font-medium border-b-2 border-primary/30',
        )}>
          {displayInput.slice(h.start, h.end)}
          {faded && (
            <span className="absolute -top-[18px] left-0 text-[9px] font-semibold tracking-[0.08em] uppercase text-primary/45 whitespace-nowrap">
              {h.label}
            </span>
          )}
        </span>
      );
      last = h.end;
    });

    if (last < displayInput.length) {
      parts.push(
        <span key="tail" className={cn('transition-all duration-700', faded ? 'opacity-25' : 'text-foreground/60')}>
          {displayInput.slice(last)}
        </span>
      );
    }

    return <>{parts}</>;
  };

  // ── Progress width ──
  const progressWidth = stage === 'parsing' ? '12%' : stage === 'identifying' ? '40%' : stage === 'structuring' ? '72%' : '100%';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{
        background: `
          radial-gradient(ellipse 60% 50% at 50% 38%, hsl(var(--primary) / 0.025), transparent 70%),
          hsl(var(--background))
        `,
      }}
    >
      <div className="w-full max-w-[600px] px-6 flex flex-col items-center">

        {/* ── Brand anchor ── */}
        <span className="text-[10px] font-medium tracking-[0.14em] uppercase text-muted-foreground/40 mb-3">
          Quentari Decision Engine
        </span>

        {/* ── Status line ── */}
        <p className={cn(
          'text-[13px] text-muted-foreground/50 mb-10 transition-opacity duration-500',
          stage === 'parsing' ? 'opacity-100' : 'opacity-60',
        )}>
          {stage === 'parsing'
            ? 'Deine Entscheidung wird analysiert'
            : stage === 'finalizing'
              ? 'Struktur wird finalisiert'
              : 'Transformation läuft'}
        </p>

        {/* ── Input transformation card ── */}
        <div className={cn(
          'w-full rounded-2xl border border-border/50 bg-card/60 backdrop-blur-sm relative overflow-hidden transition-all duration-500',
          stage === 'parsing' ? 'shadow-none' : 'shadow-sm',
        )}>
          {/* Signature top line */}
          <div className={cn(
            'absolute top-0 inset-x-0 h-[2px] transition-all duration-700 ease-out',
            stage === 'parsing'
              ? 'bg-gradient-to-r from-transparent via-primary/15 to-transparent'
              : 'bg-gradient-to-r from-transparent via-primary/30 to-transparent',
          )} />

          {/* Input text area */}
          <div className="p-6 pb-4">
            <div className="text-[13px] leading-[1.75] font-sans relative">
              {renderText()}
            </div>

            {/* Pulse indicator while parsing */}
            {stage === 'parsing' && (
              <div className="mt-4 flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary/30 animate-pulse" />
                <span className="h-1.5 w-1.5 rounded-full bg-primary/20 animate-pulse [animation-delay:150ms]" />
                <span className="h-1.5 w-1.5 rounded-full bg-primary/15 animate-pulse [animation-delay:300ms]" />
              </div>
            )}
          </div>

          {/* ── Structured extraction emergence ── */}
          <div className={cn(
            'border-t border-border/30 px-6 overflow-hidden transition-all duration-700 ease-out',
            (stage === 'structuring' || stage === 'finalizing') ? 'max-h-[200px] py-5 opacity-100' : 'max-h-0 py-0 opacity-0',
          )}>
            {structured?.optA && (
              <div className="flex items-baseline gap-3 mb-3">
                <span className="text-[9px] font-semibold tracking-[0.1em] uppercase text-primary/40 shrink-0 w-14 mt-px">
                  Option A
                </span>
                <span className="text-[13px] text-foreground/70 leading-snug">
                  {truncate(structured.optA, 75)}
                </span>
              </div>
            )}
            {structured?.optB && (
              <div className="flex items-baseline gap-3 mb-3">
                <span className="text-[9px] font-semibold tracking-[0.1em] uppercase text-primary/40 shrink-0 w-14 mt-px">
                  Option B
                </span>
                <span className="text-[13px] text-foreground/70 leading-snug">
                  {truncate(structured.optB, 75)}
                </span>
              </div>
            )}
            {stage === 'finalizing' && structured?.conflict && (
              <div className="flex items-baseline gap-3 animate-fade-in">
                <span className="text-[9px] font-semibold tracking-[0.1em] uppercase text-muted-foreground/40 shrink-0 w-14 mt-px">
                  Konflikt
                </span>
                <span className="text-[13px] text-foreground/50 leading-snug">
                  {truncate(structured.conflict, 75)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Progression steps ── */}
        <div className="mt-10 w-full space-y-2.5">
          {STEPS.map((label, i) => (
            <div
              key={i}
              className={cn(
                'flex items-center gap-3 transition-all duration-500',
                doneSteps[i] ? 'opacity-100' : 'opacity-25',
              )}
            >
              <div className={cn(
                'w-[18px] h-[18px] rounded-md flex items-center justify-center shrink-0 transition-all duration-500',
                doneSteps[i] ? 'bg-primary/8' : '',
              )}>
                {doneSteps[i] ? (
                  <Check className="h-3 w-3 text-primary/60" />
                ) : (
                  <div className="w-1 h-1 rounded-full bg-muted-foreground/25" />
                )}
              </div>
              <span className="text-[12px] text-muted-foreground/60">{label}</span>
            </div>
          ))}
        </div>

        {/* ── Progress bar ── */}
        <div className="mt-8 w-full h-[1.5px] rounded-full bg-border/20 overflow-hidden">
          <div
            className="h-full rounded-full bg-primary/20 transition-all duration-700 ease-out"
            style={{ width: progressWidth }}
          />
        </div>

        {/* ── Quality hint ── */}
        {inputQuality === 'low' && stage === 'finalizing' && (
          <p className="mt-5 text-[11px] text-muted-foreground/40 text-center animate-fade-in">
            Dein Input war knapp — prüfe die erkannten Felder besonders sorgfältig.
          </p>
        )}
      </div>
    </div>
  );
};

function truncate(str: string, max: number): string {
  if (str.length <= max) return str;
  return str.slice(0, max - 1).trimEnd() + '…';
}
